import React, { createContext, useContext, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';

const DataContext = createContext();

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const POKES_CACHE_DURATION = 30 * 1000; // 30 seconds

export function DataProvider({ children }) {
  const [discoverData, setDiscoverData] = useState(null);
  const [profileCache, setProfileCache] = useState({});
  const [pokesCache, setPokesCache] = useState(null);
  const [lastFetch, setLastFetch] = useState({});

  // DISCOVER DATA (existing)
  const loadDiscoverData = useCallback(async (userEmail) => {
    const now = Date.now();
    
    if (discoverData && (now - lastFetch.discover) < CACHE_DURATION) {
      return discoverData;
    }

    try {
      const [myTagsRes, usersRes, allTagsRes, pokesRes] = await Promise.all([
        supabase
          .from('user_tags')
          .select('tag, category')
          .eq('user_email', userEmail),
        supabase
          .from('users')
          .select('email, username, bio, profile_color, profile_image')
          .neq('email', userEmail)
          .eq('banned', false), // Hide banned users
        supabase
          .from('user_tags')
          .select('user_email, tag, category'),
        supabase
          .from('pokes')
          .select('to_user')
          .eq('from_user', userEmail),
      ]);

      const userTagsMap = {};
      allTagsRes.data?.forEach(t => {
        if (!userTagsMap[t.user_email]) {
          userTagsMap[t.user_email] = [];
        }
        userTagsMap[t.user_email].push({ tag: t.tag, category: t.category });
      });

      const usersWithTags = usersRes.data?.map(u => ({
        ...u,
        tags: userTagsMap[u.email] || [],
      })) || [];

      const newData = {
        myTags: myTagsRes.data || [],
        users: usersWithTags,
        pokes: pokesRes.data?.map(p => p.to_user) || [],
      };

      setDiscoverData(newData);
      setLastFetch(prev => ({ ...prev, discover: now }));
      return newData;
    } catch (error) {
      console.error('Error loading discover data:', error);
      return discoverData;
    }
  }, [discoverData, lastFetch]);

  const updatePokes = useCallback((newPokes) => {
    if (discoverData) {
      setDiscoverData({ ...discoverData, pokes: newPokes });
    }
  }, [discoverData]);

  // PROFILE DATA (new)
  const getMyProfile = useCallback(async (email, forceRefresh = false) => {
    const now = Date.now();
    const cached = profileCache[email];
    
    if (!forceRefresh && cached && (now - cached.fetchTime) < CACHE_DURATION) {
      return cached.data;
    }

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single();

    if (error && error.code !== 'PGRST116') throw error;

    setProfileCache(prev => ({
      ...prev,
      [email]: { data, fetchTime: now }
    }));
    
    return data;
  }, [profileCache]);

  const getMyTags = useCallback(async (email, forceRefresh = false) => {
    const cacheKey = `tags_${email}`;
    const now = Date.now();
    
    if (!forceRefresh && profileCache[cacheKey] && (now - profileCache[cacheKey].fetchTime) < CACHE_DURATION) {
      return profileCache[cacheKey].data;
    }

    const { data, error } = await supabase
      .from('user_tags')
      .select('tag, category')
      .eq('user_email', email);

    if (error) throw error;

    setProfileCache(prev => ({
      ...prev,
      [cacheKey]: { data: data || [], fetchTime: now }
    }));
    
    return data || [];
  }, [profileCache]);

  // POKES DATA (new)
  const getPokes = useCallback(async (email, forceRefresh = false) => {
    const now = Date.now();
    
    if (!forceRefresh && pokesCache && (now - lastFetch.pokes) < POKES_CACHE_DURATION) {
      return pokesCache;
    }

    const [receivedRes, sentRes] = await Promise.all([
      supabase
        .from('pokes')
        .select('from_user, created_at, seen, accepted')
        .eq('to_user', email)
        .order('created_at', { ascending: false }),
      supabase
        .from('pokes')
        .select('to_user, created_at, accepted')
        .eq('from_user', email)
        .order('created_at', { ascending: false })
    ]);

    if (receivedRes.error) throw receivedRes.error;
    if (sentRes.error) throw sentRes.error;

    const userEmails = new Set([
      ...(receivedRes.data || []).map(p => p.from_user),
      ...(sentRes.data || []).map(p => p.to_user)
    ]);

    let usersMap = {};
    if (userEmails.size > 0) {
      const { data: usersData } = await supabase
        .from('users')
        .select('email, username, bio, profile_color, profile_image, contact_info')
        .in('email', Array.from(userEmails));

      usersData?.forEach(u => {
        usersMap[u.email] = u;
      });
    }

    const enrichedReceived = (receivedRes.data || []).map(p => ({
      ...p,
      user: usersMap[p.from_user]
    }));

    const enrichedSent = (sentRes.data || []).map(p => ({
      ...p,
      user: usersMap[p.to_user]
    }));

    const result = { received: enrichedReceived, sent: enrichedSent };
    setPokesCache(result);
    setLastFetch(prev => ({ ...prev, pokes: now }));
    
    return result;
  }, [pokesCache, lastFetch]);

  // CACHE MANAGEMENT
  const clearCache = useCallback(() => {
    setDiscoverData(null);
    setProfileCache({});
    setPokesCache(null);
    setLastFetch({});
  }, []);

  const invalidateCache = clearCache; // Alias for compatibility

  const value = {
    // Discover
    discoverData,
    loadDiscoverData,
    updatePokes,
    invalidateCache,
    // Profile
    getMyProfile,
    getMyTags,
    // Pokes
    getPokes,
    // General
    clearCache,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useDataContext() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within DataProvider');
  }
  return context;
}

export function useData() {
  return useDataContext();
}